// =============================================================================
// domain/accountSetup.ts — creating an account from the "Set up account" modal.
// -----------------------------------------------------------------------------
// Ported from `buildAccountFromSetup` + `ACCOUNT_SHAPES`. This is the ONLY place
// an account is brought into existence, which is why the free-tier cap, the
// duplicate-external-id guard and the opening fee row all converge on it.
//
// ── 🔴 IT IS NOT A SIX-STEP WIZARD, WHATEVER THE HANDOFF SAID ──
//
// The rebuild notes described "+ New Account" as a six-step wizard (firm → form
// → pattern → exit model → import → pre-tag). **That path is dead code in the
// old client and its own source says so:**
//
//   ⚠ 'new-account' IS CURRENTLY UNREACHABLE — every `mode === 'new-account'`
//   branch below is dead. Traced 2026-07-22 … The account redesign pointed both
//   "+ New Account" buttons at openAccountSetupModal instead.
//
// The six steps belong to the FIRST-RUN onboarding wizard. What the button opens
// is one modal. Presence in the old client's source is not evidence a control is
// reachable — the note here was written FROM the dead branch.
//
// ── ⚠ THE THREE PLACES A "DELIBERATE NOTHING" MUST OUTRANK A BLANK ──
//
// The same principle three times, and each one was a real bug in the old client
// before it was fixed there:
//
//   feeWaived      A comped account costs $0 BY AGREEMENT. `feeAmount === 0` is
//                  indistinguishable from "never filled in", which made a free
//                  eval nag forever in the completion nudge.
//   activatedFrom  `'none'` is an ANSWER — "this funded account came from an
//                  eval that predates the app". Without it the nudge demands a
//                  link that cannot exist.
//   custom shape   `rules: null`, NOT an empty rules object. Zeroing the rules
//                  would put fake limits on the gauges instead of blanks.
// =============================================================================

import type { AccountRules, AccountType, DrawdownType } from './types';

/**
 * A starting shape. Deliberately GENERIC and naming no firm.
 *
 * ⚠ THE OLD CLIENT REMOVED PER-FIRM TEMPLATES ON PURPOSE and the reasoning is
 * load-bearing: "a stale copy would quietly hand you wrong drawdown math". These
 * feed `computeDrawdownFloor`, so a lagging copy of Apex's rules hands the trader
 * a wrong buffer and they blow an account trusting our arithmetic. Nothing here
 * claims to know any firm's current rules.
 */
export interface AccountShape {
  id: string;
  label: string;
  summary: string;
  accountSize: number | null;
  /** ⚠ NULL on `custom` by design — callers must not treat it as `{}`. */
  rules: ShapeRules | null;
}

/** The rules a shape prefills — a subset of `AccountRules`. */
export interface ShapeRules {
  maxDDType: DrawdownType;
  maxDDValue: number;
  lockMechanic: string;
  lockPad: number;
  dailyLossLimit: number;
  dllEnforcement: 'hard' | 'soft' | 'optional' | 'none';
  profitTarget: number;
  consistencyRulePct: number;
  consistencyScope: 'always' | 'eval-only' | 'funded-only' | 'none';
  minTradingDays: number;
  maxContracts: number;
  eodFlatRule: boolean;
}

export const ACCOUNT_SHAPES: readonly AccountShape[] = [
  {
    id: 'gen-25k-eod',
    label: '25K',
    summary: 'EOD trail · 1.5K target · 1K max loss',
    accountSize: 25_000,
    rules: {
      maxDDType: 'trailing-eod', maxDDValue: 1000,
      lockMechanic: 'trail-then-freeze-at-start', lockPad: 0,
      dailyLossLimit: 500, dllEnforcement: 'optional',
      profitTarget: 1500, consistencyRulePct: 50, consistencyScope: 'always',
      minTradingDays: 0, maxContracts: 3, eodFlatRule: false,
    },
  },
  {
    id: 'gen-50k-eod',
    label: '50K',
    summary: 'EOD trail · 3K target · 2K max loss',
    accountSize: 50_000,
    rules: {
      maxDDType: 'trailing-eod', maxDDValue: 2000,
      lockMechanic: 'trail-then-freeze-at-start', lockPad: 0,
      dailyLossLimit: 1000, dllEnforcement: 'optional',
      profitTarget: 3000, consistencyRulePct: 50, consistencyScope: 'always',
      minTradingDays: 0, maxContracts: 5, eodFlatRule: false,
    },
  },
  {
    id: 'gen-100k-intraday',
    label: '100K',
    summary: 'Intraday trail · 6K target · 3K max loss',
    accountSize: 100_000,
    rules: {
      maxDDType: 'trailing-intraday', maxDDValue: 3000,
      lockMechanic: 'trail-forever', lockPad: 0,
      dailyLossLimit: 2000, dllEnforcement: 'optional',
      profitTarget: 6000, consistencyRulePct: 50, consistencyScope: 'always',
      minTradingDays: 0, maxContracts: 10, eodFlatRule: false,
    },
  },
  {
    id: 'gen-150k-static',
    label: '150K',
    summary: 'Static DD · 9K target · 5K max loss',
    accountSize: 150_000,
    rules: {
      maxDDType: 'static', maxDDValue: 5000,
      lockMechanic: 'static', lockPad: 0,
      dailyLossLimit: 3000, dllEnforcement: 'optional',
      profitTarget: 9000, consistencyRulePct: 50, consistencyScope: 'always',
      minTradingDays: 0, maxContracts: 15, eodFlatRule: false,
    },
  },
  {
    // The escape hatch. `rules: null` is the SIGNAL, not an oversight.
    id: 'custom',
    label: 'Custom',
    summary: "I'll enter the rules myself",
    accountSize: null,
    rules: null,
  },
];

/**
 * Every rules key a shape must define.
 *
 * ⚠ A LIST, NOT A COMMENT, and the old client says why: `dllEnforcement` and
 * `consistencyScope` went missing from a shape once, and two separate readers
 * then defaulted them OPPOSITELY. Here the type makes it a compile error, and
 * `shapeRulesAreComplete` makes it a test failure too — belt and braces, because
 * a `ShapeRules` cast would defeat the first one.
 */
export const SHAPE_REQUIRED_RULE_KEYS: readonly (keyof ShapeRules)[] = [
  'maxDDType', 'maxDDValue', 'lockMechanic', 'lockPad',
  'dailyLossLimit', 'dllEnforcement',
  'profitTarget', 'consistencyRulePct', 'consistencyScope',
  'minTradingDays', 'maxContracts', 'eodFlatRule',
];

export function accountShapeById(id: string): AccountShape | null {
  return ACCOUNT_SHAPES.find((s) => s.id === id) ?? null;
}

/** True when a shape defines every required key. `custom` is exempt — it has none. */
export function shapeRulesAreComplete(s: AccountShape): boolean {
  if (!s.rules) return s.id === 'custom';
  return SHAPE_REQUIRED_RULE_KEYS.every((k) => s.rules![k] !== undefined);
}

/**
 * A shape's rules widened to a full `AccountRules`.
 *
 * ⚠ The fields a shape does NOT carry (`payoutSchedule`, `payoutsTaken`,
 * `scalingRules`, `prohibitedProducts`) are filled with their empty values here
 * rather than left absent, because `AccountRules` is the contract the writer and
 * every reader share — an absent key is what let `consistencyScope` vanish in
 * the old client.
 */
/**
 * A rules block in which NOTHING is configured.
 *
 * 🚨 EVERY VALUE HERE IS THE ONE THAT ALREADY MEANS "not set" TO EVERY READER,
 * and that is a checked claim rather than an intention. The gauges test
 * `Number(r.maxDDValue) > 0`, the loss-room bar tests `Number(r.dailyLossLimit)
 * || 0`, and the edit form seeds itself with `r?.maxDDValue ?? 0` and
 * `r?.maxDDType ?? 'trailing-intraday'` — so a block of these is
 * indistinguishable from `rules: null` on every surface. ⚠ `maxDDType` is
 * therefore `null`, NOT the `'static'` that `TEMPLATE_RULE_DEFAULTS` carries:
 * 'static' would silently preselect a drawdown type in the editor that the
 * trader never chose.
 *
 * ▶ It exists ONLY so a typed daily loss limit survives on a shape with no
 * rules of its own. `custom` carries `rules: null` deliberately — a `{}` there
 * "puts a fake 0 limit on every gauge" — and the limit was being dropped on the
 * one shape that has no other source for it. Tracker #32.
 */
const UNSET_RULES: AccountRules = {
  maxDDType: null, maxDDValue: 0,
  lockMechanic: null, lockPad: null,
  dailyLossLimit: 0, dllEnforcement: 'optional',
  profitTarget: 0, consistencyRulePct: 0, consistencyScope: 'always',
  minTradingDays: 0, payoutSchedule: '', payoutsTaken: 0,
  maxContracts: 0, eodFlatRule: false,
  scalingRules: '', prohibitedProducts: [],
};

export function shapeRulesFor(id: string): AccountRules | null {
  const s = accountShapeById(id);
  if (!s || !s.rules) return null;
  return {
    ...s.rules,
    payoutSchedule: '',
    payoutsTaken: 0,
    scalingRules: '',
    prohibitedProducts: [],
  };
}

// ---------------------------------------------------------------------------
// The trader's own saved templates
// ---------------------------------------------------------------------------

/**
 * A template the USER saved from an account they had already set up correctly.
 *
 * 🚨 IT IS NOT A FIRM'S RULES AND NOTHING MAY PRESENT IT AS ONE. Beta removed
 * per-firm templates because "a lagging copy hands the trader a wrong drawdown
 * buffer and they blow an account trusting our math", and its audit automation
 * had been silently switched off for six weeks while the UI still claimed the
 * templates were "audited daily". These carry no such claim: they are the
 * trader's own numbers, stamped with when they saved them.
 *
 * ⚠ `savedAt` IS LOAD-BEARING, not decoration. A template saved fourteen months
 * ago carries exactly the staleness risk that killed the firm ones, so the modal
 * shows its age and the copy says to check it.
 */
export interface PropTemplate {
  id: string;
  label: string;
  summary: string;
  firm: string;
  accountSize: number | null;
  /** ISO timestamp. Null when a template predates the stamp. */
  savedAt: string | null;
  /**
   * ⚠ A FULL rules block, unlike a built-in shape's twelve-key subset — a
   * template is saved FROM an account, so it carries whatever that account had,
   * including `lockMechanic: null`. Josh's one real template does exactly that.
   */
  rules: AccountRules | null;
}

/** Ids of user templates are prefixed, so a picker can tell them from a shape. */
export const USER_TEMPLATE_PREFIX = 'ut_';

export const isUserTemplateId = (id: string): boolean =>
  String(id ?? '').startsWith(USER_TEMPLATE_PREFIX);

/**
 * Parse the opaque `prop_templates` array.
 *
 * ⚠ A malformed entry is DROPPED, not defaulted. A template with no rules would
 * offer to prefill an account with nothing and leave every gauge unset while
 * looking like it had applied — worse than not offering it.
 */
export function parsePropTemplates(raw: unknown): PropTemplate[] {
  if (!Array.isArray(raw)) return [];
  const out: PropTemplate[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const id = typeof o['id'] === 'string' ? o['id'] : '';
    const label = typeof o['label'] === 'string' ? o['label'].trim() : '';
    if (!id || !label) continue;
    const rules = o['rules'];
    if (!rules || typeof rules !== 'object') continue;
    out.push({
      id,
      label,
      summary: typeof o['summary'] === 'string' ? o['summary'] : '',
      firm: typeof o['firm'] === 'string' ? o['firm'] : '',
      accountSize: Number.isFinite(Number(o['accountSize'])) ? Number(o['accountSize']) : null,
      savedAt: typeof o['savedAt'] === 'string' ? o['savedAt'] : null,
      rules: rules as AccountRules,
    });
  }
  return out;
}

/**
 * "13d ago" — the age the modal prints beside a saved template.
 *
 * ⚠ TAKES `now` AS A PARAMETER. `domain/` never reads a clock (D26), and this
 * one has to be testable at a fixed instant anyway.
 *
 * ⚠ It rounds DOWN and never says "today" for something saved days ago. The
 * whole point of the stamp is to make an old template look old, so the rounding
 * must not flatter it.
 */
export function templateAge(savedAt: string | null, nowIso: string): string {
  if (!savedAt) return 'age unknown';
  const then = Date.parse(savedAt);
  const now = Date.parse(nowIso);
  if (!Number.isFinite(then) || !Number.isFinite(now)) return 'age unknown';
  const days = Math.floor((now - then) / 86_400_000);
  if (days < 0) return 'just saved';
  if (days === 0) return 'today';
  if (days === 1) return '1d ago';
  if (days < 60) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 24) return `${months}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

// ---------------------------------------------------------------------------
// Saving one — capturing an account the trader already configured
// ---------------------------------------------------------------------------

/**
 * "1.5K" · "25K" · "500". Matches how the built-in summaries read, so a saved
 * template sits in the same list without looking foreign.
 */
export function compactMoney(n: number | null | undefined): string {
  const v = Math.abs(Number(n) || 0);
  if (v >= 1000) {
    const k = v / 1000;
    return `${Number.isInteger(k) ? k : Math.round(k * 10) / 10}K`;
  }
  return String(v);
}

/** The one-line description under a template's name. */
export function propTemplateSummary(
  rules: Partial<AccountRules> | null | undefined,
  size: number | null | undefined,
): string {
  const r = rules ?? {};
  const dd = r.maxDDType
    ? ({
      'trailing-eod': 'EOD trail',
      'trailing-intraday': 'Intraday trail',
      static: 'Static',
    } as Record<string, string>)[r.maxDDType] ?? String(r.maxDDType)
    : '—';
  const bits = [dd];
  if (Number(r.profitTarget) > 0) bits.push(`${compactMoney(r.profitTarget)} target`);
  if (Number(r.maxDDValue) > 0) bits.push(`${compactMoney(r.maxDDValue)} max loss`);
  if (Number(size) > 0) bits.unshift(compactMoney(size));
  return bits.join(' · ');
}

/**
 * What the SAVE DIALOG shows as "this is what you are storing".
 *
 * 🚨 IT IS NOT `propTemplateSummary`, AND THE DIFFERENCE WAS FOUND ON THE RUNNING
 * PAGE, NOT IN A TEST. The stored summary prints the drawdown type, the profit
 * target and the max loss — and nothing else. On Josh's Sim1 those are
 * `trailing-intraday`, `0` and `0`, so the summary reads *"50K · Intraday trail"*
 * while the one rule the account actually carries is a **$500 daily loss limit**.
 * The account qualifies to be saved BECAUSE of that limit, and the preview of
 * what would be saved did not mention it.
 *
 * ⚠ THE STORED `summary` IS DELIBERATELY LEFT ALONE. It is a field both clients
 * write and both read, and Josh's one real template carries the old client's
 * exact string — changing the format here would make a template saved in one
 * client read differently from an identical one saved in the other, in a list
 * that shows them side by side. So the extra fact is added at the point of
 * DECISION rather than to the stored record.
 *
 * ⚠ The daily limit is not a minor rule: it drives the card's "Today's Loss
 * Room" bar, and it is the number a trader would most want to know had come
 * along for the ride.
 */
export function templateSaveDescription(
  rules: Partial<AccountRules> | null | undefined,
  size: number | null | undefined,
): string {
  const base = propTemplateSummary(rules, size);
  const dll = Number(rules?.dailyLossLimit) || 0;
  return dll > 0 ? `${base} · ${compactMoney(dll)} daily cap` : base;
}

/**
 * What a missing rules key becomes when a template is captured.
 *
 * 🚨 COMPLETENESS IS FILLED, NOT ASSUMED. An account that predates a rules key —
 * or one that arrived by import — can be missing `dllEnforcement` or
 * `consistencyScope`, and those two are precisely the pair where `undefined`
 * meant OPPOSITE things in two different engines in the old client. Saving such
 * an account verbatim would mint a template that reintroduces the ambiguity into
 * every account made from it.
 *
 * ⚠ `lockMechanic: 'trail-forever'` is the CONSERVATIVE default, not the
 * friendly one. Freezing the floor at the starting balance gives the trader more
 * room later; trailing it for ever gives less. A default that errs toward more
 * room is the one that gets an evaluation blown.
 */
export const TEMPLATE_RULE_DEFAULTS: ShapeRules = {
  maxDDType: 'static', maxDDValue: 0,
  lockMechanic: 'trail-forever', lockPad: 0,
  dailyLossLimit: 0, dllEnforcement: 'optional',
  profitTarget: 0, consistencyRulePct: 0, consistencyScope: 'always',
  minTradingDays: 0, maxContracts: 0, eodFlatRule: false,
};

/** The fields of an account a template is built from. */
export interface TemplateSource {
  name: string;
  firm: string;
  accountSize: number;
  rules: Partial<AccountRules> | null;
}

/**
 * Is there anything worth saving?
 *
 * ⚠ A personal or SIM account has no drawdown, target or daily limit, so the
 * menu entry would mint an all-zeros template that helps nobody and then sits in
 * the picker looking like a real one. The old client gates the menu item on
 * exactly this.
 *
 * ⚠ Takes the three fields STRUCTURALLY rather than a `TemplateSource`, because
 * the card that decides whether to show the menu entry holds the narrowed
 * `CardAccount`, whose rules are nullable. Widening here beats casting there.
 */
export function accountHasSaveableRules(
  a: {
    rules?: {
      maxDDValue?: number | null;
      profitTarget?: number | null;
      dailyLossLimit?: number | null;
    } | null;
  } | null | undefined,
): boolean {
  const r = a?.rules ?? {};
  return Number(r.maxDDValue) > 0 || Number(r.profitTarget) > 0 || Number(r.dailyLossLimit) > 0;
}

/** "Apex 100K" — what the naming box opens with. */
export function suggestTemplateLabel(a: TemplateSource): string {
  const bits = [a.firm, a.accountSize ? compactMoney(a.accountSize) : ''].filter(Boolean);
  return (bits.join(' ') || a.name || 'My template').trim().slice(0, 40);
}

export interface BuildTemplateInput {
  account: TemplateSource;
  label: string;
  /** Minted by the caller — `domain/` neither generates ids nor reads a clock (D26). */
  id: string;
  /** ISO instant, passed in for the same reason. */
  savedAt: string;
}

/**
 * Capture a configured account as a reusable template.
 *
 * That gesture — capture what you already got right — IS the feature. The
 * alternative is a blank template builder, which asks the trader to type every
 * rule a second time and gives them a second chance to get one wrong.
 *
 * ⚠ THE OPTIONAL RULE FIELDS RIDE ALONG. `payoutSchedule`, `scalingRules` and
 * `prohibitedProducts` are not part of the twelve-key shape contract, but they
 * round-trip through the account writer, so dropping them here would quietly
 * lose data the trader entered.
 */
export function buildPropTemplate(input: BuildTemplateInput): PropTemplate {
  const src = input.account.rules ?? {};
  const rules: Record<string, unknown> = {};
  for (const k of SHAPE_REQUIRED_RULE_KEYS) {
    const v = (src as Record<string, unknown>)[k];
    rules[k] = v !== undefined ? v : TEMPLATE_RULE_DEFAULTS[k];
  }
  for (const k of ['payoutSchedule', 'scalingRules', 'prohibitedProducts'] as const) {
    const v = (src as Record<string, unknown>)[k];
    if (v !== undefined) rules[k] = v;
  }

  const size = Number(input.account.accountSize) || 0;
  const label = String(input.label ?? '').trim().slice(0, 40)
    || input.account.name
    || 'My template';

  return {
    id: input.id,
    label,
    summary: propTemplateSummary(rules as Partial<AccountRules>, size),
    firm: input.account.firm || '',
    accountSize: size,
    savedAt: input.savedAt,
    rules: rules as unknown as AccountRules,
  };
}

/**
 * Is this template complete enough to store?
 *
 * The same gate the built-in shapes pass, applied to a captured one — because
 * `buildPropTemplate` fills every required key, a failure here means the filler
 * itself is broken, and that is worth saying out loud rather than storing a
 * half-template that hands out blank gauges later.
 */
export function propTemplateIsComplete(t: PropTemplate | null | undefined): boolean {
  if (!t || !t.rules) return false;
  const r = t.rules as unknown as Record<string, unknown>;
  return SHAPE_REQUIRED_RULE_KEYS.every((k) => r[k] !== undefined);
}

// ---------------------------------------------------------------------------
// Editing the stored list — on the RAW array, never on parsed copies
// ---------------------------------------------------------------------------
//
// 🚨 `parsePropTemplates` BUILDS A NEW OBJECT WITH THE SEVEN KEYS IT KNOWS. So a
// parse → edit → write round trip would silently drop any key it does not model,
// on every template in the list — including the ones the trader was not touching.
// That is D62 ("never send a column you don't own") one level down, inside a
// JSONB array, where the type system cannot help at all.
//
// So both editors below work on the RAW entries: they read `id` and `label`
// defensively and otherwise pass each stored object through untouched.

const rawLabel = (item: unknown): string =>
  (item && typeof item === 'object' && typeof (item as Record<string, unknown>)['label'] === 'string'
    ? String((item as Record<string, unknown>)['label'])
    : '').trim().toLowerCase();

const rawId = (item: unknown): string =>
  (item && typeof item === 'object' && typeof (item as Record<string, unknown>)['id'] === 'string'
    ? String((item as Record<string, unknown>)['id'])
    : '');

export interface UpsertResult {
  next: unknown[];
  /** The id of the template that was REPLACED, or null when this was an append. */
  replacedId: string | null;
}

/**
 * Put a template into the stored list.
 *
 * 🚨 A MATCHING LABEL REPLACES, IT DOES NOT APPEND — and this is a deliberate
 * departure from the old client, which pushes unconditionally. Its own source
 * declines to build a rename with the reasoning *"re-saving from the same
 * account is one click"* — but re-saving there produces a SECOND row with the
 * same name, and the picker then shows two entries a human cannot tell apart
 * except by their age stamps. The stated update path did not update anything.
 *
 * ⚠ Matching is on the trimmed, case-folded label because that is the only thing
 * the trader can see in the picker. Two rows reading "Apex 100K" are ambiguous
 * whatever their ids say.
 */
export function upsertPropTemplate(raw: unknown, tpl: PropTemplate): UpsertResult {
  const list = Array.isArray(raw) ? [...(raw as unknown[])] : [];
  const want = tpl.label.trim().toLowerCase();
  const at = list.findIndex((item) => rawLabel(item) === want);
  if (at === -1) return { next: [...list, tpl], replacedId: null };
  const replacedId = rawId(list[at]);
  list[at] = tpl;
  return { next: list, replacedId: replacedId || null };
}

export interface RemoveResult {
  next: unknown[];
  removed: boolean;
}

/** Drop one template by id. `removed: false` means it was already gone. */
export function removePropTemplate(raw: unknown, id: string): RemoveResult {
  const list = Array.isArray(raw) ? (raw as unknown[]) : [];
  const next = list.filter((item) => rawId(item) !== id);
  return { next, removed: next.length !== list.length };
}

// ---------------------------------------------------------------------------
// The build
// ---------------------------------------------------------------------------

/** The modal's two account types. The stored model has five. */
export type SetupType = 'eval' | 'funded';

/**
 * ⚠ THE MODAL CAN ONLY EXPRESS TWO OF THE FIVE STORED TYPES, and the old client
 * carries a scar from it: writing the control's value back unconditionally
 * SILENTLY CONVERTED `sim-funded`, `personal` and legacy `funded` accounts into
 * evals, because they all display as "eval" with nothing better to show. Any
 * edit path must therefore track whether the user actually chose.
 */
export const SETUP_TYPE_MAP: Record<SetupType, AccountType> = {
  eval: 'combine',
  funded: 'live-funded',
};

export type FeeCadence = 'once' | 'monthly' | 'free';

export interface SetupInput {
  /** Omitted when creating; the caller mints the id. */
  id?: string;
  accountType: SetupType;
  shapeId: string;
  name: string;
  firm: string;
  externalId: string;
  /**
   * Only read when the chosen shape supplies no size of its own — which today
   * means `custom`, the one shape that carries `accountSize: null` by design.
   *
   * 🚨 IT EXISTS BECAUSE WITHOUT IT `custom` WAS UNCOMPLETABLE (tracker #32).
   * Size had always come from the shape, so a Custom account was born at 0 —
   * and 0 is `startingBalance` and `currentBalance` too, so every drawdown
   * gauge, the loss-room bar and Risk of Ruin measured against nothing. The
   * card then demanded "account size" for ever and the surface its own
   * "Finish setup" link opens had no field that could supply one.
   *
   * ⚠ A shape's own size WINS. This is a fallback, never an override: a stale
   * value left in the field must not quietly resize a built-in shape.
   */
  accountSize?: string | number;
  feeAmount: string | number;
  feeCadence: FeeCadence;
  /** `'none'` is a real answer — see the header. */
  activatedFrom: string;
  dailyLossLimit: string | number;
  /** Today, ISO. Passed in — `domain/` never reads a clock (D26). */
  today: string;
}

export interface SetupAdjustment {
  date: string;
  type: 'Eval fee' | 'Activation fee';
  /** Negative — money out, the established convention. */
  amount: number;
  note: string;
}

export interface SetupResult {
  account: {
    name: string;
    firm: string;
    accountType: AccountType;
    accountSize: number;
    startingBalance: number;
    currentBalance: number;
    status: 'active';
    autoBalance: true;
    externalId: string;
    rules: AccountRules | null;
    feeWaived: boolean;
    feeCadence: string;
    feeAmount: number;
    activatedFrom: string | null;
  };
  adjustments: SetupAdjustment[];
  /** Field keys that failed validation. Empty means saveable. */
  errors: string[];
}

/**
 * Turn the modal's fields into an account plus its opening fee row.
 *
 * ⚠ PURE, and it mints no id and reads no clock. The old client calls `uid()`
 * and `now()` inside its version, which is what makes that one untestable
 * without stubbing globals — and this module has to stay importable into a Deno
 * Edge Function (D26).
 */
export function buildAccountFromSetup(input: SetupInput): SetupResult {
  const errors: string[] = [];
  const name = String(input.name ?? '').trim();
  const firm = String(input.firm ?? '').trim();
  if (!name) errors.push('name');

  const shape = accountShapeById(input.shapeId);
  if (!shape) errors.push('shape');

  const rules = shape ? shapeRulesFor(shape.id) : null;
  // ⚠ The shape wins; the typed value is only the fallback for a shape that
  // carries none. See `SetupInput.accountSize` — tracker #32.
  const size = Number(shape?.accountSize) || Math.abs(Number(input.accountSize) || 0);
  const accountType = SETUP_TYPE_MAP[input.accountType] ?? 'combine';

  // ⚠ FREE IS A FLAG, NEVER A ZERO — see the header.
  const feeWaived = input.feeCadence === 'free';
  const feeAmount = feeWaived ? 0 : Math.abs(Number(input.feeAmount) || 0);
  const cadence = input.feeCadence === 'monthly' ? 'monthly' : 'once';

  // The one RULE the setup flow collects, because it drives the card's Today's
  // Loss Room bar and is the field most likely to differ from a generic shape.
  // ⚠ Applied ONLY when supplied, so a blank cannot wipe a limit already tuned
  // in the full edit form.
  const dll = Math.abs(Number(input.dailyLossLimit) || 0);
  const withDll: AccountRules | null = dll > 0
    ? { ...(rules ?? UNSET_RULES), dailyLossLimit: dll }
    : rules;

  const adjustments: SetupAdjustment[] = [];
  // ⚠ ONE fee field, and its TYPE follows the account. A funded account's cost is
  // an activation fee; an eval's is an eval fee. The old client notes there is
  // deliberately no second input, because a separate activation field asked for
  // the same money twice.
  const feeType: SetupAdjustment['type'] =
    accountType === 'live-funded' ? 'Activation fee' : 'Eval fee';
  if (feeAmount > 0) {
    adjustments.push({
      date: input.today,
      type: feeType,
      amount: -feeAmount,
      note: cadence === 'monthly'
        ? `${firm || 'Account'} monthly subscription`
        : `${firm || 'Account'} ${feeType.toLowerCase()}`,
    });
  }

  return {
    account: {
      name,
      firm,
      accountType,
      accountSize: size,
      startingBalance: size,
      currentBalance: size,
      status: 'active',
      autoBalance: true,
      externalId: String(input.externalId ?? '').trim(),
      rules: withDll,
      feeWaived,
      feeCadence: feeWaived ? '' : cadence,
      feeAmount,
      // ⚠ Only on a funded account, and `'none'` is stored as null — the SENTINEL
      // is a UI answer meaning "no eval to link", not a value worth persisting.
      activatedFrom: accountType === 'live-funded'
        && input.activatedFrom
        && input.activatedFrom !== 'none'
        ? input.activatedFrom
        : null,
    },
    adjustments,
    errors,
  };
}

/**
 * Does an external id already belong to another account?
 *
 * ⚠ THE SAME GUARD THE FULL EDIT FORM USES, and it must live beside the build
 * rather than in the modal: the external id is what auto-routes imported trades,
 * so a second account claiming one silently splits a book in two. The old client
 * does not error here — it closes the modal and says it will use the existing
 * account instead, which is the friendlier answer to a user who forgot they
 * already added it.
 */
export function findExternalIdOwner<T extends { id: string; name: string; externalId: string }>(
  externalId: string,
  accounts: readonly T[],
  selfId?: string,
): T | null {
  const want = String(externalId ?? '').trim().toLowerCase();
  if (!want) return null;
  return accounts.find(
    (a) => a.id !== selfId && String(a.externalId ?? '').trim().toLowerCase() === want,
  ) ?? null;
}
